import { Device } from '../helpers/Device'
import WebModel from '../models/WebModel'
import { MediaType } from '../enums/MediaType'

export namespace Flooring {
  export const getDisplayFloor = (model: IWebViewModel | WebModel) => {
    return getFloor(model, MediaType.banner)
  }

  export const getVideoFloor = (model: IWebViewModel | WebModel) => {
    return getFloor(model, MediaType.video)
  }
}

/** Ensure only results of this function are used by consuming agents */
type ComputedDevice = string & { flag: true }
function computeDevice(): ComputedDevice {
  if (Device.deviceType === 'desktop') {
    return Device.deviceType as ComputedDevice
  }

  const { deviceType, os } = Device
  const normalizedDevice = DeviceMap[deviceType]
  const normalizedOS = OSMap[os]

  return `${normalizedDevice} - ${normalizedOS}` as ComputedDevice
}

interface IGetIndexesArgs {
  device: ComputedDevice
  country: CountryMap
  category: CategoryMap
  mediaType: MediaType
}
function getIndexes({
  device,
  country,
  category,
  mediaType
}: IGetIndexesArgs): {
  device: number
  country: number
  category: number
  mediaType: number
} {
  const defaultValue = 1.00001
  const categoryDefaultValue = 1.08
  const indexes = {
    device: DeviceIndexes[device] || defaultValue,
    country: CountryIndexes[country] || defaultValue,
    category: CategoryIndexes[category] || categoryDefaultValue,
    mediaType: MediaTypeIndexes[mediaType]
  }

  return indexes
}

const baseIndex = 0.5
export function getFloor(
  model: IWebViewModel | WebModel,
  mediaType: MediaType
): number {
  const device = computeDevice()
  const country = CountryMap[model.country || CountryMap.US] || CountryMap.US
  const category =
    CategoryMap[model.topLevelCategory && model.topLevelCategory.slug] ||
    CategoryMap.None
  const indexes = getIndexes({ device, country, category, mediaType })

  const floor = Object.keys(indexes).reduce(
    (acc, key) => acc * indexes[key],
    baseIndex
  )

  return parseFloat(floor.toFixed(2))
}

export const DeviceIndexes = {
  amp: 0.79,
  desktop: 1.36,
  other: 0.64,
  'phone - android': 1.43,
  'phone - ios': 0.61,
  'tablet - android': 1.47,
  'tablet - ios': 0.77
}

export const MediaTypeIndexes = {
  [MediaType.video]: 4,
  [MediaType.banner]: 0.44
}

export const CategoryIndexes = {
  None: 1.08,
  'Arts & Entertainment': 0.65,
  Education: 0.79,
  'Family & Parenting': 0.94,
  'Food & Drink': 1.06,
  'Health & Fitness': 1.01,
  'Hobbies & Interests': 0.85,
  'Home & Garden': 1.3,
  'Personal Finance': 1.46,
  Pets: 1.3,
  'Style & Fashion': 1.01,
  Travel: 0.92
}

export const CountryIndexes = {
  'United States': 1.63,
  International: 0.52
}

export enum CountryMap {
  US = 'United States',
  INTL = 'International'
}

export enum OSMap {
  iOS = 'ios',
  Android = 'android',
  desktop = 'desktop'
}

export enum DeviceMap {
  mobile = 'phone',
  tablet = 'tablet',
  desktop = 'desktop'
}

/** Maps slugs to display values being used by Cynthia */
export enum CategoryMap {
  None = 'None',
  'arts-and-entertainment' = 'Arts & Entertainment',
  'education' = 'Education',
  'family-and-parenting' = 'Family & Parenting',
  'food-and-drink' = 'Food & Drink',
  'health-and-fitness' = 'Health & Fitness',
  'hobbies-and-interests' = 'Hobbies & Interests',
  'home-and-garden' = 'Home & Garden',
  'personal-finance' = 'Personal Finance',
  'pets' = 'Pets',
  'style-and-fashion' = 'Style & Fashion',
  'travel' = 'Travel'
}
